import axios from '@api';
import { normalizeTopicIds } from '@helpers';

import {
  buildFlowStateUpdatePayload,
  buildNarrativeDraftUpdatePayload,
  buildTopicIdsUpdatePayload,
  hydrateDynamicNarrativeFlowState,
  normalizeChatMessageInput,
  normalizeFlowStateForUpdate,
  normalizeTokenUsage,
  transformChatSessionData,
  transformFlowStateData,
  transformNarrativeDraft,
  transformProject,
  transformProjectFlows,
  transformProjectListResponse,
} from './utilities/helpers';
import chatApi from './api';

export { hydrateDynamicNarrativeFlowState } from './utilities/helpers';

/**
 * Create a new chat session for the user on the chat service.
 * Chat session is authenticated and created for the user
 * based on the JWT header with the user ID in its claims.
 * Responds with the chat session UUID that was created,
 * which will be used for adding messages to and reading the chat history.
 * @return {Promise<string>} chatSessionId
 */
export const createChatSession = () => {
  return chatApi({
    data: {},
    method: 'post',
    url: '/api/v1/chat',
  }).then(({ data }) => {
    const sessionId = data?.chatSessionId;

    if (!sessionId) {
      throw new Error('Chat service did not include a session identifier in its response.');
    }
    return sessionId;
  });
};

/**
 * Sends a chat message to the chat service.
 * @param {Object} options
 * @param {string} options.chatSessionId
 * @param {string} options.userInput
 * @return {Promise<any>}
 */
export const createChatMessage = ({ chatSessionId, userInput }) => {
  return chatApi({
    data: JSON.stringify(normalizeChatMessageInput(userInput)),
    method: 'post',
    url: `/api/v1/chat/${chatSessionId}`,
  }).then(({ data }) => data);
};

/**
 * Fetches token usage for the current user's client from Insights.
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 * @return {Promise<any>}
 */
export const readTokenUsage = ({ signal } = {}) => {
  return axios({
    method: 'get',
    signal,
    url: '/v2/api/token-allocations/usage',
    withCredentials: true,
  })
    .then(({ data }) => normalizeTokenUsage(data))
    .catch((error) => {
      if (error?.response?.data?.detail !== 'No active token allocation found.') throw error;
      return normalizeTokenUsage(error.response.data);
    });
};

/**
 * Fetches chat messages from the chat service and transforms them for UI.
 * @param {Object} options
 * @param {string} options.chatSessionId
 * @param {(role: string) => string} [options.createFallbackId]
 * @param {AbortSignal} [options.signal]
 * @return {Promise<{messages: Array<{id: string, role: string, content: string, isStreaming?: boolean}>}>}
 */
export const readChatSession = ({ chatSessionId, createFallbackId, signal }) => {
  return chatApi({
    method: 'get',
    signal,
    url: `/api/v1/chat/${chatSessionId}`,
  }).then(({ data }) => transformChatSessionData(data, { createFallbackId }));
};

/**
 * Fetches flow-state for a chat session and hydrates prompt metadata for UI.
 * @param {Object} options
 * @param {string} options.chatSessionId
 * @param {AbortSignal} [options.signal]
 * @return {Promise<{flowId: number|null, flowState: Object|null}>}
 */
export const readFlowState = ({ chatSessionId, signal }) => {
  return chatApi({
    method: 'get',
    signal,
    url: `/api/v1/flow-state/${chatSessionId}`,
  })
    .then(({ data }) => transformFlowStateData(data))
    .then(async ({ flowId, flowState }) => ({
      flowId,
      flowState: await hydrateDynamicNarrativeFlowState({ flowState, signal }),
    }));
};

/**
 * Fetches the saved Narrative Draft for a project.
 *
 * @param {Object} options
 * @param {number|string} options.projectId Insights project identifier
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{editablePromptAnswers: Object, narrativeName: string, topics: Array<string|number>}>}
 */
export const readNarrativeDraft = ({ projectId, signal }) => {
  if (!projectId) {
    return Promise.reject(new Error('projectId is required to read the narrative draft.'));
  }

  return axios({
    method: 'get',
    signal,
    url: `/v2/api/narrative-drafts/${projectId}`,
    withCredentials: true,
  }).then(({ data }) => transformNarrativeDraft(data));
};

/**
 * Saves a Narrative Draft and requests backend propagation to Chat flow-state.
 *
 * @param {Object} options
 * @param {Object} options.draft draft data in the UI shape
 * @param {number|string} options.projectId Insights project identifier
 * @returns {Promise<{editablePromptAnswers: Object, narrativeName: string, topics: Array<string|number>}>}
 */
export const updateNarrativeDraft = ({ draft = {}, projectId }) => {
  if (!projectId) {
    return Promise.reject(new Error('projectId is required to update the narrative draft.'));
  }

  return axios({
    data: {
      ...buildNarrativeDraftUpdatePayload({ draft }),
      projectId,
    },
    method: 'put',
    url: '/v2/api/narrative-drafts/',
    withCredentials: true,
  }).then(({ data }) => transformNarrativeDraft(data));
};

/**
 * Clears a project's saved Narrative Draft.
 *
 * @param {Object} options
 * @param {number|string} options.projectId Insights project identifier
 * @returns {Promise<void>}
 */
export const clearNarrativeDraft = ({ projectId }) => {
  if (!projectId) {
    return Promise.reject(new Error('projectId is required to clear the narrative draft.'));
  }

  return axios({
    method: 'put',
    url: `/v2/api/narrative-drafts/${projectId}/clear`,
    withCredentials: true,
  }).then(() => undefined);
};

/**
 * Deletes a project's saved Narrative Draft without resetting a focused run Narrative.
 *
 * @param {Object} options
 * @param {number|string} options.projectId Insights project identifier
 * @returns {Promise<void>}
 */
export const deleteNarrativeDraft = ({ projectId }) => {
  if (!projectId) {
    return Promise.reject(new Error('projectId is required to delete the narrative draft.'));
  }

  return axios({
    method: 'delete',
    url: `/v2/api/narrative-drafts/${projectId}/delete`,
    withCredentials: true,
  }).then(() => undefined);
};

/**
 * Clears a Narrative Flow's canvas state before the Flow is removed.
 *
 * @param {Object} options
 * @param {number|string} options.flowId Narrative Flow identifier
 * @returns {Promise<void>}
 */
export const clearNarrativeFlow = ({ flowId }) => {
  if (!flowId) {
    return Promise.reject(new Error('flowId is required to clear the narrative flow.'));
  }

  return axios({
    method: 'put',
    url: `/v2/api/flows/${flowId}/clear`,
    withCredentials: true,
  }).then(() => undefined);
};

/**
 * Creates a project tied to a chat session.
 *
 * @param {Object} options
 * @param {string} options.chatSessionId chat session identifier
 * @param {Array<number>} [options.flowIds=[]] flows to associate on create
 * @param {string} [options.name] project name
 * @returns {Promise<ReturnType<typeof transformProject>>}
 */
export const createProject = ({ chatSessionId, flowIds = [], name }) => {
  return axios({
    data: {
      chat_session_id: chatSessionId,
      flow_ids: flowIds,
      name,
    },
    method: 'post',
    url: '/v2/api/projects',
    withCredentials: true,
  }).then(({ data }) => transformProject(data));
};

/**
 * Fetches a project by its Chat session identifier.
 *
 * @param {Object} options
 * @param {string} options.chatSessionId Chat session identifier
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<ReturnType<typeof transformProject>>}
 */
export const readProject = ({ chatSessionId, signal }) => {
  return axios({
    method: 'get',
    signal,
    url: `/v2/api/projects/${chatSessionId}`,
    withCredentials: true,
  }).then(({ data }) => transformProject(data));
};

/**
 * Fetches narratives (flows) for a project chat session.
 *
 * @param {Object} options
 * @param {number|string} options.chatSessionId project chat session identifier
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Array<Object>>}
 */
export const readProjectFlows = ({ chatSessionId, signal }) => {
  return axios({
    method: 'get',
    signal,
    url: `/v2/api/projects/${chatSessionId}/flows`,
    withCredentials: true,
  }).then(({ data }) => transformProjectFlows(data));
};

/**
 * Adds a flow to an existing project.
 *
 * @param {Object} options
 * @param {number|string} options.flowId flow identifier
 * @param {number|string} options.projectId project identifier
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const addFlowToProject = ({ flowId, projectId }) => {
  return axios({
    method: 'put',
    url: `/v2/api/projects/${projectId}/add/${flowId}`,
    withCredentials: true,
  });
};

/**
 * Removes a flow from a project.
 *
 * @param {Object} options
 * @param {number|string} options.flowId flow identifier
 * @param {number|string} options.projectId project identifier
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const removeFlowFromProject = ({ flowId, projectId }) => {
  return axios({
    method: 'put',
    url: `/v2/api/projects/${projectId}/remove/${flowId}`,
    withCredentials: true,
  });
};

/**
 * Updates chat flow-state topic ids for a draft.
 *
 * @param {Object} options
 * @param {string} options.chatSessionId chat session identifier
 * @param {Array<string|number>} options.topicIds ordered list of topic/component identifiers for draft flow state
 * @returns {Promise<void>}
 */
export const updateFlowStateTopicIds = ({ chatSessionId, topicIds = [] }) => {
  if (!chatSessionId) {
    return Promise.reject(new Error('chatSessionId is required to update the flow state.'));
  }

  return chatApi({
    data: buildTopicIdsUpdatePayload({ topicIds: normalizeTopicIds(topicIds) }),
    method: 'put',
    url: `/api/v1/flow-state/${chatSessionId}`,
  }).then(() => undefined);
};

/**
 * Updates draft flow-state fields stored for a chat session.
 *
 * @param {Object} options
 * @param {string} options.chatSessionId chat session identifier
 * @param {Object} options.flowState partial flow-state payload to persist
 * @returns {Promise<void>}
 */
export const updateFlowState = ({ chatSessionId, flowState = {} }) => {
  if (!chatSessionId) {
    return Promise.reject(new Error('chatSessionId is required to update the flow state.'));
  }

  return chatApi({
    data: buildFlowStateUpdatePayload({ flowState: normalizeFlowStateForUpdate(flowState) }),
    method: 'put',
    url: `/api/v1/flow-state/${chatSessionId}`,
  }).then(() => undefined);
};

/**
 * Sets the focused Narrative Flow for a Chat session.
 *
 * @param {Object} options
 * @param {string} options.chatSessionId chat session identifier
 * @param {number|string|null} options.flowId Narrative Flow identifier, or null to clear focus
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<void>}
 */
export const focusNarrativeFlow = ({ chatSessionId, flowId, signal }) => {
  if (!chatSessionId) {
    return Promise.reject(new Error('chatSessionId is required to focus a narrative flow.'));
  }
  if (flowId === undefined) {
    return Promise.reject(new Error('flowId is required to focus a narrative flow.'));
  }

  return chatApi({
    data: {
      focus_flow_id: flowId,
    },
    method: 'put',
    ...(signal ? { signal } : {}),
    url: `/api/v1/flow-state/${chatSessionId}/focus-flow`,
  }).then(() => undefined);
};

/**
 * Get project list for Narratives Project History Window
 * @return {Promise}
 */
export const readProjectList = ({ signal }) => {
  return axios({
    method: 'get',
    signal,
    url: '/v2/api/projects',
    withCredentials: true,
  }).then(({ data }) => transformProjectListResponse(data));
};

/**
 * Updates the display name of a project.
 *
 * @param {Object} options
 * @param {string} options.name new project name
 * @param {number|string} options.chatSessionId chat session identifier
 * @returns {Promise<ReturnType<typeof transformProject>>}
 */
export const renameProject = ({ chatSessionId, name }) => {
  return axios({
    data: { name },
    method: 'put',
    url: `/v2/api/projects/${chatSessionId}/rename`,
    withCredentials: true,
  }).then(({ data }) => transformProject({ ...data, chat_session_id: chatSessionId, name }));
};

/**
 * Soft deletes a project.
 *
 * @param {Object} options
 * @param {number|string} options.projectId project identifier
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const deleteProject = ({ projectId }) => {
  return axios({
    method: 'delete',
    url: `/v2/api/projects/${projectId}/delete`,
    withCredentials: true,
  });
};

/**
 * Update favorite status for a project in Narratives Project History Window
 * @param {Object} options
 * @param {string} options.chatSessionId
 * @param {boolean} options.isFavorite
 * @return {Promise<ReturnType<typeof transformProject>>}
 */
export const favoriteProject = ({ chatSessionId, isFavorite }) => {
  return axios({
    data: { is_favorite: isFavorite },
    method: 'post',
    url: `/v2/api/projects/${chatSessionId}/favorite`,
    withCredentials: true,
  }).then(({ data }) => transformProject({ ...data, chat_session_id: chatSessionId }));
};
